'use client';

import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

// Icon components for features
const LightningIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="22"
    viewBox="0 0 18 22"
    fill="none"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.2316 0.362745C10.8636 -0.395255 12.0946 0.122745 11.9946 1.10475L11.2886 8.00275H16.9996C17.1896 8.00282 17.3757 8.05702 17.536 8.15902C17.6963 8.26101 17.8243 8.40657 17.9049 8.57865C17.9855 8.75073 18.0153 8.94221 17.991 9.13066C17.9667 9.31912 17.8892 9.49674 17.7676 9.64275L7.76763 21.6427C7.13563 22.4007 5.90463 21.8827 6.00463 20.9007L6.71063 14.0027H0.999626C0.809612 14.0027 0.62355 13.9485 0.46323 13.8465C0.30291 13.7445 0.174967 13.5989 0.0943826 13.4268C0.0137986 13.2548 -0.0160903 13.0633 0.00821571 12.8748C0.0325217 12.6864 0.110017 12.5087 0.231626 12.3627L10.2316 0.362745ZM3.13463 12.0027H8.48263C8.53853 12.0027 8.59381 12.0145 8.64491 12.0371C8.696 12.0598 8.74178 12.093 8.77928 12.1344C8.81678 12.1759 8.84517 12.2247 8.86263 12.2778C8.88008 12.3309 8.88622 12.3871 8.88063 12.4427L8.32763 17.8477L14.8646 10.0027H9.51663C9.46072 10.0027 9.40544 9.99103 9.35435 9.96835C9.30325 9.94567 9.25748 9.91253 9.21997 9.87108C9.18247 9.82962 9.15408 9.78076 9.13662 9.72765C9.11917 9.67454 9.11304 9.61837 9.11863 9.56275L9.67163 4.15875L3.13463 12.0027Z"
      fill="#05F273"
    />
  </svg>
);

const ShieldCheckIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="25"
    viewBox="0 0 25 25"
    fill="none"
    className={className}
  >
    <path
      d="M4 5.65V13C4 20.35 13 23.5 13 23.5C13 23.5 22 20.35 22 13V5.65L13 2.5L4 5.65Z"
      stroke="#05F273"
      strokeWidth="2"
      strokeLinecap="square"
    />
    <path
      d="M9 13.329L11.827 16.157L17.484 10.5"
      stroke="#05F273"
      strokeWidth="2"
      strokeLinecap="square"
    />
  </svg>
);

const ChartIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="25"
    viewBox="0 0 25 25"
    fill="none"
    className={className}
  >
    <path
      d="M2.21429 19C1.732 19 1.3259 18.8422 0.996 18.5265C0.665333 18.2116 0.5 17.824 0.5 17.3636C0.5 16.9033 0.665333 16.5156 0.996 16.2007C1.32667 15.8858 1.73276 15.728 2.21429 15.7273C2.35829 15.7273 2.49124 15.7378 2.61314 15.7589C2.73505 15.78 2.85124 15.8298 2.96171 15.9084L8.68972 10.4407C8.60743 10.3353 8.55524 10.2244 8.53314 10.108C8.51105 9.99164 8.5 9.86473 8.5 9.72727C8.5 9.26691 8.66533 8.87927 8.996 8.56436C9.3259 8.24873 9.732 8.09091 10.2143 8.09091C10.6966 8.09091 11.1027 8.24873 11.4326 8.56436C11.7625 8.88 11.9278 9.26764 11.9286 9.72727C11.9286 9.81964 11.8802 10.0436 11.7834 10.3993L15.2257 13.684C15.3354 13.6331 15.444 13.5975 15.5514 13.5771C15.6596 13.556 15.7853 13.5455 15.9286 13.5455C16.0718 13.5455 16.2013 13.556 16.3171 13.5771C16.4314 13.5982 16.5362 13.648 16.6314 13.7265L21.26 9.30836C21.1785 9.21745 21.1267 9.11709 21.1046 9.00727C21.0825 8.89745 21.0714 8.77382 21.0714 8.63636C21.0714 8.176 21.2368 7.78836 21.5674 7.47345C21.8973 7.15782 22.3034 7 22.7857 7C23.268 7 23.6741 7.15782 24.004 7.47345C24.3347 7.78836 24.5 8.176 24.5 8.63636C24.5 9.09673 24.3347 9.48436 24.004 9.79927C23.6733 10.1142 23.2672 10.272 22.7857 10.2727C22.6425 10.2727 22.513 10.2622 22.3971 10.2411C22.2829 10.22 22.1777 10.1702 22.0817 10.0916L17.4531 14.5098C17.5354 14.6007 17.5876 14.7011 17.6097 14.8109C17.6318 14.9207 17.6429 15.0444 17.6429 15.1818C17.6429 15.6422 17.4775 16.0298 17.1469 16.3447C16.817 16.6604 16.4109 16.8182 15.9286 16.8182C15.4463 16.8182 15.0402 16.6604 14.7103 16.3447C14.3804 16.0291 14.215 15.6415 14.2143 15.1818C14.2143 15.0451 14.2253 14.9182 14.2474 14.8011C14.2695 14.684 14.3217 14.5731 14.404 14.4684L10.9617 11.1836C10.852 11.2615 10.7358 11.3109 10.6131 11.332C10.4912 11.3531 10.3583 11.3636 10.2143 11.3636C10.1175 11.3636 9.88324 11.3175 9.51143 11.2251L3.78343 16.6927C3.83676 16.7975 3.8741 16.9011 3.89543 17.0036C3.91752 17.1069 3.92857 17.2269 3.92857 17.3636C3.92857 17.824 3.76324 18.2116 3.43257 18.5265C3.10267 18.8422 2.69657 19 2.21429 19Z"
      fill="#05F273"
    />
  </svg>
);

// Map of icons to cycle through for features
const FEATURE_ICONS = [LightningIcon, ShieldCheckIcon, ChartIcon];

/**
 * Props for `FeaturePoints`.
 */
export type FeaturePointsProps =
  SliceComponentProps<Content.FeaturePointsSlice>;

/**
 * Component for "FeaturePoints" Slices.
 */
const FeaturePoints: FC<FeaturePointsProps> = ({ slice }) => {
  const { title, description, features } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative bg-[#0B2B26] py-20 px-4 md:px-8 lg:px-[150px]"
    >
      <div className="max-w-[1140px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-start lg:items-center">
          {/* Left Column - Title and Description */}
          <div className="flex flex-col gap-6 w-full lg:w-[610px] lg:flex-shrink-0">
            {isFilled.richText(title) && (
              <div className="text-white text-[32px] md:text-[42px] font-bold leading-[1.2]">
                <PrismicRichText field={title} />
              </div>
            )}
            {isFilled.richText(description) && (
              <div className="text-white text-base md:text-[18px] leading-[1.5]">
                <PrismicRichText field={description} />
              </div>
            )}
          </div>

          {/* Right Column - Feature Cards */}
          <div className="flex flex-col gap-6 w-full lg:flex-1 lg:min-w-0">
            {features?.map((feature, index) => {
              const IconComponent = FEATURE_ICONS[index % FEATURE_ICONS.length];

              return (
                <div
                  key={`feature-${index}`}
                  className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.4)] to-transparent p-6 rounded-2xl shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)]"
                >
                  <div className="flex flex-col gap-6">
                    {/* Feature Header with Icon */}
                    <div className="flex gap-2 items-center">
                      <div className="w-[25px] h-[25px] flex-shrink-0">
                        <IconComponent />
                      </div>
                      <h3 className="text-[#05F273] text-xl md:text-[24px] font-bold leading-[1.4]">
                        {feature.feature_title}
                      </h3>
                    </div>
                    {/* Feature Description */}
                    {isFilled.richText(feature.feature_description) && (
                      <div className="text-white text-base md:text-[18px] leading-[1.4]">
                        <PrismicRichText field={feature.feature_description} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturePoints;
